/**
 * Charlie — mock data layer.
 *
 * The brief and My Tasks are live now (`charlieApi.ts`); what is left here
 * backs the payor board, whose endpoint doesn't exist yet, and stands in for
 * the whole screen when there is no service line to query with. `ApiProvider`
 * spreads this one, so a panel goes live by overriding a single method.
 *
 * RULE carried over from the prototype: every signal/feed maps to an EXISTING
 * DayLight metric or its record drill. Charlie never invents a number — each
 * suggestion names the metric that raised it.
 */
import { BUCKET_LABELS } from "@/lib/utils/charlie/constants";
import type {
  BucketKey,
  CharlieProvider,
  CharlieService,
  CharlieTask,
  PayorRow,
  Suggestion,
} from "../types";

const iso = (d: Date) => d.toISOString().slice(0, 10);
/** The demo is anchored to a fixed morning rather than the real clock: the
 *  narrative copy inside the record drills ("SOC 8/27", "day-5 deadline Sep 1")
 *  is written against this date, so a moving `today` would put the dates and
 *  the prose out of step. */
const today = new Date("2026-08-31T08:00:00");
const plus = (n: number) => iso(new Date(today.getTime() + n * 864e5));

/** The fixtures group on the fixed `BucketKey` vocabulary; `bucketLabel` is
 *  attached on the way out, from `BUCKET_LABELS`. */
const demoSuggestions: Omit<Suggestion, "bucketLabel">[] = [
  {
    id: "stf-kim", svc: "hh", bucket: "staffing", branch: "Northside", deadline: "soft", defaultDue: plus(0),
    title: "Fill 3 open visit slots — D. Kim (RN), Productivity Attainment 84% this week",
    signal: "Productivity Attainment %",
    records: {
      title: "D. Kim · RN · Northside", subtitle: "Attainment 84% · 3 open slots",
      rows: [["Tue Sep 1 · 10:00", "Open slot"], ["Wed Sep 2 · 09:00", "Open slot"], ["Thu Sep 3 · 14:00", "Open slot"]],
    },
  },
  {
    id: "stf-rom", svc: "hh", bucket: "staffing", branch: "East Bay", deadline: "soft", defaultDue: plus(0),
    title: "Rebalance 2 visits off L. Romano (PT), Productivity Attainment 112% — overtime risk",
    signal: "Productivity Attainment %",
    records: {
      title: "L. Romano · PT · East Bay", subtitle: "Attainment 112% · overtime risk",
      rows: [["Wed Sep 2", "6 visits scheduled (cap 5)"], ["Fri Sep 4", "6 visits scheduled (cap 5)"]],
    },
  },
  {
    id: "noa-1", svc: "hh", bucket: "noa", branch: "Northside", deadline: "hard", defaultDue: plus(1),
    title: "File NOA — R. Alvarez (SOC 4 days ago, day-5 deadline Sep 1)",
    signal: "NOA Late (6+ Days) % · NOA Days to Submit",
    records: {
      title: "R. Alvarez", subtitle: "SOC 8/27 · Medicare FFS",
      rows: [["SOC date", "Aug 27"], ["Day-5 deadline", "Sep 1"], ["Status", "Chart complete, NOA pending"]],
    },
  },
  {
    id: "noa-2", svc: "hh", bucket: "noa", branch: "East Bay", deadline: "hard", defaultDue: plus(1),
    title: "File NOA — M. Pham (SOC 4 days ago, day-5 deadline Sep 1)",
    signal: "NOA Late (6+ Days) %",
    records: {
      title: "M. Pham", subtitle: "SOC 8/27 · Medicare Advantage",
      rows: [["SOC date", "Aug 27"], ["Day-5 deadline", "Sep 1"], ["Status", "Awaiting insurance verification"]],
    },
  },
  {
    id: "lupa-1", svc: "hh", bucket: "lupa", branch: "Northside", deadline: "hard", defaultDue: plus(3),
    title: "Add one visit for 6 patients whose period ends this week one visit under the LUPA threshold",
    signal: "LUPA Rate 7.1% (target ≤6%)",
    records: {
      title: "LUPA-at-risk periods (6)", subtitle: "Ends ≤ Thu · one visit under threshold",
      rows: [
        ["J. Morales · PT", "4 of 5 · ends Wed"], ["T. Okoye · PT", "4 of 5 · ends Wed"],
        ["R. Fisk · PT", "5 of 6 · ends Thu"], ["A. Chen · PT", "4 of 5 · ends Thu"],
        ["B. Nassar · SN", "3 of 4 · ends Thu"], ["L. Grady · SN", "3 of 4 · ends Thu"],
      ],
    },
  },
  {
    id: "soc-1", svc: "hh", bucket: "soc", branch: "Northside", deadline: "hard", defaultDue: plus(0),
    title: "Schedule SOC for 3 accepted referrals older than 48 hours",
    signal: "SOC Visit in 48 hrs % 89% (target ≥95%)",
    records: {
      title: "Accepted referrals without SOC (3)", subtitle: "All past 48 hours",
      rows: [
        ["J. Ortiz", "61h · Medicare FFS · St. Mary's"], ["B. Lee", "52h · Commercial · Valley Ortho"],
        ["A. Grant", "49h · Medicare FFS · Community"],
      ],
    },
  },
  {
    id: "pay-1", svc: "hh", bucket: "payor", branch: "Northside", deadline: "soft", defaultDue: plus(4),
    title: 'Confirm acceptance policy for Medicare Advantage — 4 declined this week ("rate / no contract"), 2 from St. Mary\'s',
    signal: "Payor Acceptance Board (Referrals · Non-Admits by payor)",
    records: {
      title: "Declined MA referrals (4)", subtitle: "Reason: rate / no contract",
      rows: [
        ["St. Mary's — pt 1", "Declined 8/26"], ["St. Mary's — pt 2", "Declined 8/27"],
        ["Valley Ortho", "Declined 8/28"], ["Community", "Declined 8/29"],
      ],
    },
  },
  {
    id: "ord-1", svc: "hh", bucket: "orders", branch: "East Bay", deadline: "soft", defaultDue: plus(0),
    title: "Call Dr. Patel's office — 7 orders unsigned over 12 days",
    signal: "Sent to Signed (days) 16d (target ≤12d)",
    records: {
      title: "Unsigned orders — Dr. Patel (7)", subtitle: "All sent > 12 days",
      rows: [
        ["485 / POC · R. Alvarez", "16d"], ["PO · T. Okoye", "15d"], ["POC Update · A. Chen", "14d"],
        ["PO · L. Grady", "14d"], ["485 / POC · M. Pham", "13d"], ["PO · J. Morales", "13d"],
        ["PO · B. Nassar", "13d"],
      ],
    },
  },
  {
    id: "cli-1", svc: "hh", bucket: "clinical", branch: "Northside", deadline: "soft", defaultDue: plus(2),
    title: "Post-transfer review — E. Brooks and H. Wu went to the hospital yesterday",
    signal: "Hospitalization % (TIF)",
    records: {
      title: "Hospital transfers — yesterday (2)", subtitle: "TIF assessments filed",
      rows: [["E. Brooks", "ED → admitted · dyspnea"], ["H. Wu", "Fall → admitted"]],
    },
  },
  /* ── hospice ────────────────────────────────────────────────────────────── */
  {
    id: "h-stf-1", svc: "hos", bucket: "staffing", branch: "Northside", deadline: "soft", defaultDue: plus(0),
    title: "Fill 2 open visit slots — R. Ellis (RN), Productivity Attainment 86% this week",
    signal: "Productivity Attainment %",
    records: {
      title: "R. Ellis · RN · Northside", subtitle: "Attainment 86% · 2 open slots",
      rows: [["Tue Sep 1 · 13:00", "Open slot"], ["Thu Sep 3 · 09:00", "Open slot"]],
    },
  },
  {
    id: "h-hope-1", svc: "hos", bucket: "hope", branch: "East Bay", deadline: "hard", defaultDue: plus(2),
    title: "3 HOPE assessments approaching their submission deadline",
    signal: "HOPE Past Due (Unlocked) · Avg Days to Lock HOPE",
    records: {
      title: "HOPE assessments due (3)", subtitle: "Window closes ≤3 days",
      rows: [
        ["C. Ramos · HOPE Update", "due Sep 2 · K. Boyd RN"], ["F. Liu · Admission HOPE", "due Sep 3 · R. Ellis RN"],
        ["G. Ade · HOPE Update", "due Sep 3 · K. Boyd RN"],
      ],
    },
  },
  {
    id: "h-soc-1", svc: "hos", bucket: "hsoc", branch: "Northside", deadline: "hard", defaultDue: plus(0),
    title: "Dispatch admission nurse — 2 accepted referrals without a SOC visit (26h, 31h)",
    signal: "Avg Days Pending (To Admit)",
    records: {
      title: "Accepted referrals without SOC visit (2)", subtitle: "Hospice same/next-day standard",
      rows: [["M. Trent", "26h · Memorial Hosp"], ["S. Cole", "31h · Grace SNF"]],
    },
  },
  {
    id: "h-ls-1", svc: "hos", bucket: "longstay", branch: "East Bay", deadline: "soft", defaultDue: plus(4),
    title: "Recert / eligibility review — 4 patients over 180 days",
    signal: "Median Length of Stay · Avg Length of Stay",
    records: {
      title: "Patients over 180 days (4)", subtitle: "Recert / eligibility review",
      rows: [["D. Fox", "212d · RHC"], ["N. Iyer", "198d · RHC"], ["H. Blake", "191d · RHC"], ["T. Marsh", "184d · RHC"]],
    },
  },
  {
    id: "h-cli-1", svc: "hos", bucket: "hclinical", branch: "Northside", deadline: "soft", defaultDue: plus(2),
    title: "Review 2 live discharges from yesterday — reason and pattern",
    signal: "Live Discharge Rate · Live Discharges",
    records: {
      title: "Live discharges — yesterday (2)", subtitle: "Review reason and pattern",
      rows: [["R. Quinn", "Revocation — hospital"], ["A. Sato", "No longer terminally ill"]],
    },
  },
];

const demoTasks: CharlieTask[] = [
  {
    id: "t1", svc: "hh", branch: "Northside", bucketLabel: "Referrals Pending",
    title: "Disposition 2 pending referrals from Valley Ortho",
    notes: "Waiting on F2F from Dr. Shah — Jess following up, I'll close it",
    due: plus(0), status: "open", createdAt: "2026-08-28",
  },
  {
    id: "t2", svc: "hh", branch: "East Bay", bucketLabel: "Utilization Review",
    title: "Review PT utilization — 6 episodes over plan",
    notes: "", due: plus(4), status: "open", createdAt: "2026-08-27",
  },
];

const demoPayor: Record<CharlieService, PayorRow[]> = {
  hh: [
    { payor: "Medicare FFS", referred: 86, accepted: 79, declined: 4, policy: "Accept" },
    { payor: "Medicare Advantage", referred: 41, accepted: 28, declined: 11, policy: "Review" },
    { payor: "Medicaid", referred: 14, accepted: 9, declined: 4, policy: "Review" },
    { payor: "Commercial", referred: 12, accepted: 11, declined: 1, policy: "Accept" },
  ],
  hos: [
    { payor: "Medicare FFS", referred: 54, accepted: 49, declined: 3, policy: "Accept" },
    { payor: "Medicare Advantage", referred: 18, accepted: 14, declined: 3, policy: "Review" },
    { payor: "Medicaid", referred: 6, accepted: 6, declined: 0, policy: "Accept" },
  ],
};

/** Latency the real endpoints would have, so loading states are exercised. */
const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Module-level so tasks created in one session survive a service switch (which
 *  refetches) — the same thing a real tasks store would do. */
let mockTasks: CharlieTask[] = [...demoTasks];

export const MockProvider: CharlieProvider = {
  name: "mock",
  // The fixtures predate the SP's free-form categories, so their bucket keys
  // are looked up in the fixed label table. Live rows carry their own label.
  feed: ({ svc }) =>
    wait(120).then(() => ({
      suggestions: demoSuggestions
        .filter((s) => s.svc === svc)
        .map((s) => ({ ...s, bucketLabel: BUCKET_LABELS[s.bucket as BucketKey] ?? s.bucket })),
      tasks: [...mockTasks],
    })),
  acceptSuggestion: async (sug, { notes, due }) => {
    const task: CharlieTask = {
      id: sug.id,
      svc: sug.svc,
      branch: sug.branch,
      bucketLabel: sug.bucketLabel,
      title: sug.title,
      notes,
      due,
      status: "open",
      createdAt: iso(today),
    };
    mockTasks = [task, ...mockTasks];
    return task;
  },
  updateTask: async (id, patch) => {
    mockTasks = mockTasks.map((t) => (t.id === id ? { ...t, ...patch } : t));
    return mockTasks.find((t) => t.id === id);
  },
  rejectSuggestion: async (id, reason) => ({ id, reason, at: iso(today) }),
  // The fixtures carry their records inline — there is no drill to fetch.
  drillDown: async (sug) => sug.records,
  payorBoard: ({ svc }) => wait(80).then(() => demoPayor[svc].map((r) => ({ ...r }))),
  savePayorPolicy: async (svc, payor, policy) => ({ svc, payor, policy }),
};
